import { useCallback, useEffect, useState } from 'react'
import type { UserAnswerDraft, UserAnswersDraft } from '../../state/types'
import { createTimestamps, updateTimestamps } from '../../state/types'
import type { Question } from '@/features/events/shared/types'

interface UserAnswerPayload {
  readonly question: string
  readonly selectedIndices: number[]
  readonly selectedAnswers: string[]
  readonly customAnswer: string | null
}

interface AnswersPayload {
  readonly answers: Readonly<Record<string, UserAnswerPayload>>
}

interface UseQuestionnaireStateParams {
  readonly eventId: string
  readonly questions: readonly Question[]
  readonly userAnswersDraft: UserAnswersDraft | null
  readonly onSaveDraft: (answers: ReadonlyMap<string, UserAnswerDraft>) => void
  readonly onSubmit: (payload: AnswersPayload) => Promise<void>
  readonly onSubmitSuccess: () => void
  readonly onError: (error: Error) => void
}

interface UseQuestionnaireStateReturn {
  readonly answers: ReadonlyMap<string, UserAnswerDraft>
  readonly validationErrors: ReadonlyMap<string, string>
  readonly showError: boolean
  readonly errorMessage: string
  readonly customInputDialogQuestion: Question | null
  readonly customInputDialogQuestionIndex: number | null
  readonly isSubmitting: boolean
  readonly handleRadioChange: (
    questionHeader: string,
    questionText: string,
    optionIndex: number,
    optionLabel: string,
  ) => void
  readonly handleCheckboxChange: (
    questionHeader: string,
    questionText: string,
    optionIndex: number,
    optionLabel: string,
  ) => void
  readonly handleCustomAnswerChange: (
    questionHeader: string,
    questionText: string,
    customText: string,
    isMultiSelect: boolean,
  ) => void
  readonly handleOpenCustomInputDialog: (question: Question, questionIndex: number) => void
  readonly handleCloseCustomInputDialog: () => void
  readonly handleSubmitCustomInput: (customText: string) => Promise<boolean>
  readonly handleSubmitAnswers: () => Promise<void>
  readonly setShowError: (show: boolean) => void
}

function useQuestionnaireState({
  eventId,
  questions,
  userAnswersDraft,
  onSaveDraft,
  onSubmit,
  onSubmitSuccess,
  onError,
}: UseQuestionnaireStateParams): UseQuestionnaireStateReturn {
  const [answers, setAnswers] = useState<Map<string, UserAnswerDraft>>(() => {
    const map = new Map<string, UserAnswerDraft>()

    if (userAnswersDraft?.eventId === eventId) {
      for (const [key, answerDraft] of Object.entries(userAnswersDraft.answers)) {
        map.set(key, {
          question: answerDraft.question,
          selectedIndices: answerDraft.selectedIndices,
          selectedAnswers: answerDraft.selectedAnswers,
          customAnswer: answerDraft.customAnswer,
          createdAt: answerDraft.createdAt,
          updatedAt: answerDraft.updatedAt,
        })
      }
    }

    return map
  })

  const [validationErrors, setValidationErrors] = useState<Map<string, string>>(new Map())
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [customInputDialogQuestion, setCustomInputDialogQuestion] = useState<Question | null>(null)
  const [customInputDialogQuestionIndex, setCustomInputDialogQuestionIndex] = useState<
    number | null
  >(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (userAnswersDraft?.eventId !== eventId) {
      return
    }

    setAnswers(() => {
      const newMap = new Map<string, UserAnswerDraft>()
      for (const [key, answerDraft] of Object.entries(userAnswersDraft.answers)) {
        newMap.set(key, {
          question: answerDraft.question,
          selectedIndices: answerDraft.selectedIndices,
          selectedAnswers: answerDraft.selectedAnswers,
          customAnswer: answerDraft.customAnswer,
          createdAt: answerDraft.createdAt,
          updatedAt: answerDraft.updatedAt,
        })
      }
      return newMap
    })
  }, [userAnswersDraft, eventId])

  const handleRadioChange = useCallback(
    (questionHeader: string, questionText: string, optionIndex: number, optionLabel: string) => {
      setAnswers((prev) => {
        const newAnswers = new Map(prev)
        const existingAnswer = prev.get(questionHeader)
        newAnswers.set(questionHeader, {
          question: questionText,
          selectedIndices: [optionIndex],
          selectedAnswers: [optionLabel],
          customAnswer: null,
          ...updateTimestamps(existingAnswer),
        })
        onSaveDraft(newAnswers)
        return newAnswers
      })
      setValidationErrors((prev) => {
        const newErrors = new Map(prev)
        newErrors.delete(questionHeader)
        return newErrors
      })
    },
    [onSaveDraft],
  )

  const handleCheckboxChange = useCallback(
    (questionHeader: string, questionText: string, optionIndex: number, optionLabel: string) => {
      setAnswers((prev) => {
        const newAnswers = new Map(prev)
        const existingAnswer = newAnswers.get(questionHeader)
        const currentAnswer = existingAnswer || {
          question: questionText,
          selectedIndices: [],
          selectedAnswers: [],
          customAnswer: null,
          ...createTimestamps(),
        }

        const isSelected = currentAnswer.selectedIndices.includes(optionIndex)
        const newSelectedIndices = isSelected
          ? currentAnswer.selectedIndices.filter((i) => i !== optionIndex)
          : [...currentAnswer.selectedIndices, optionIndex]
        const newSelectedAnswers = isSelected
          ? currentAnswer.selectedAnswers.filter((l) => l !== optionLabel)
          : [...currentAnswer.selectedAnswers, optionLabel]

        newAnswers.set(questionHeader, {
          question: questionText,
          selectedIndices: newSelectedIndices,
          selectedAnswers: newSelectedAnswers,
          customAnswer: currentAnswer.customAnswer,
          ...updateTimestamps(existingAnswer),
        })
        onSaveDraft(newAnswers)
        return newAnswers
      })
      setValidationErrors((prev) => {
        const newErrors = new Map(prev)
        newErrors.delete(questionHeader)
        return newErrors
      })
    },
    [onSaveDraft],
  )

  const handleCustomAnswerChange = useCallback(
    (questionHeader: string, questionText: string, customText: string, isMultiSelect: boolean) => {
      setAnswers((prev) => {
        const newAnswers = new Map(prev)
        const existingAnswer = newAnswers.get(questionHeader)
        const currentAnswer = existingAnswer || {
          question: questionText,
          selectedIndices: [],
          selectedAnswers: [],
          customAnswer: null,
          ...createTimestamps(),
        }

        newAnswers.set(questionHeader, {
          question: questionText,
          selectedIndices: isMultiSelect ? currentAnswer.selectedIndices : [],
          selectedAnswers: isMultiSelect ? currentAnswer.selectedAnswers : [],
          customAnswer: customText.trim() || null,
          ...updateTimestamps(existingAnswer),
        })
        onSaveDraft(newAnswers)
        return newAnswers
      })
      setValidationErrors((prev) => {
        const newErrors = new Map(prev)
        newErrors.delete(questionHeader)
        return newErrors
      })
    },
    [onSaveDraft],
  )

  const handleOpenCustomInputDialog = useCallback((question: Question, questionIndex: number) => {
    setCustomInputDialogQuestion(question)
    setCustomInputDialogQuestionIndex(questionIndex)
  }, [])

  const handleCloseCustomInputDialog = useCallback(() => {
    setCustomInputDialogQuestion(null)
    setCustomInputDialogQuestionIndex(null)
  }, [])

  const handleSubmitCustomInput = useCallback(
    async (customText: string) => {
      if (!customInputDialogQuestion || customInputDialogQuestionIndex === null) return false

      handleCustomAnswerChange(
        customInputDialogQuestion.header,
        customInputDialogQuestion.question,
        customText,
        customInputDialogQuestion.multiSelect,
      )
      setCustomInputDialogQuestion(null)
      setCustomInputDialogQuestionIndex(null)
      return true
    },
    [customInputDialogQuestion, customInputDialogQuestionIndex, handleCustomAnswerChange],
  )

  const validateAnswers = useCallback((): boolean => {
    const errors = new Map<string, string>()
    for (const question of questions) {
      const answer = answers.get(question.header)
      if (!answer || (answer.selectedIndices.length === 0 && !answer.customAnswer)) {
        errors.set(question.header, 'Please select an option or provide custom input')
      }
    }
    setValidationErrors(errors)
    return errors.size === 0
  }, [answers, questions])

  const generateAnswersJSON = useCallback((): AnswersPayload => {
    const answersRecord: Record<string, UserAnswerPayload> = {}
    for (const question of questions) {
      const existingAnswer = answers.get(question.header)
      if (existingAnswer) {
        answersRecord[question.header] = {
          question: existingAnswer.question,
          selectedIndices: existingAnswer.selectedIndices,
          selectedAnswers: existingAnswer.selectedAnswers,
          customAnswer: existingAnswer.customAnswer,
        }
      } else {
        answersRecord[question.header] = {
          question: question.question,
          selectedIndices: [],
          selectedAnswers: [],
          customAnswer: null,
        }
      }
    }
    return { answers: answersRecord }
  }, [answers, questions])

  const handleSubmitAnswers = useCallback(async () => {
    if (!validateAnswers()) {
      return
    }

    setIsSubmitting(true)
    try {
      const payload = generateAnswersJSON()
      await onSubmit(payload)
      onSubmitSuccess()
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to submit answers')
      setErrorMessage(err.message)
      setShowError(true)
      onError(err)
    } finally {
      setIsSubmitting(false)
    }
  }, [validateAnswers, generateAnswersJSON, onSubmit, onSubmitSuccess, onError])

  return {
    answers,
    validationErrors,
    showError,
    errorMessage,
    customInputDialogQuestion,
    customInputDialogQuestionIndex,
    isSubmitting,
    handleRadioChange,
    handleCheckboxChange,
    handleCustomAnswerChange,
    handleOpenCustomInputDialog,
    handleCloseCustomInputDialog,
    handleSubmitCustomInput,
    handleSubmitAnswers,
    setShowError,
  }
}

export { useQuestionnaireState }
export type {
  UseQuestionnaireStateParams,
  UseQuestionnaireStateReturn,
  AnswersPayload,
  UserAnswerPayload,
}
